/**
 * OAuth Service
 * 
 * Handles social OAuth authentication for Google, Apple, and Microsoft.
 * Exchanges provider tokens with backend for JWT access/refresh tokens.
 */

import apiClient from './api';
import authService from './auth';

// OAuth Client IDs (loaded from environment or hardcoded for now)
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID || '';
const MICROSOFT_CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID || '';

/**
 * Initialize Google Sign-In SDK
 */
export const initGoogleAuth = () => {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve(window.google.accounts.id);
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts?.id) {
        resolve(window.google.accounts.id);
      } else {
        reject(new Error('Failed to load Google Identity Services'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Google SDK'));
    document.head.appendChild(script);
  });
};

/**
 * Trigger Google Sign-In and get credential
 */
export const signInWithGoogle = async () => {
  const googleAuth = await initGoogleAuth();
  
  return new Promise((resolve, reject) => {
    googleAuth.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => {
        if (response.credential) {
          resolve(response.credential);
        } else {
          reject(new Error('Google sign-in failed'));
        }
      },
    });
    
    googleAuth.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // Fallback to button click if One Tap is not available
        googleAuth.renderButton(
          document.createElement('div'),
          { theme: 'outline', size: 'large' }
        );
      }
    });
  });
};

/**
 * Initialize Apple Sign-In
 */
export const initAppleAuth = () => {
  return new Promise((resolve, reject) => {
    if (window.AppleID?.auth) {
      resolve(window.AppleID.auth);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => {
      if (window.AppleID?.auth) {
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: `${window.location.origin}/auth/apple/callback`,
          usePopup: true,
        });
        resolve(window.AppleID.auth);
      } else {
        reject(new Error('Failed to load Apple Sign-In'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Apple SDK'));
    document.head.appendChild(script);
  });
};

/**
 * Trigger Apple Sign-In
 */
export const signInWithApple = async () => {
  const appleAuth = await initAppleAuth();
  
  try {
    const response = await appleAuth.signIn();
    return {
      token: response.authorization.id_token,
      user: response.user, // Contains name on first login only
    };
  } catch (error) {
    throw new Error('Apple sign-in failed: ' + error.message);
  }
};

/**
 * Initialize Microsoft MSAL
 */
let msalInstance = null;

export const initMicrosoftAuth = async () => {
  if (msalInstance) {
    return msalInstance;
  }

  // Load MSAL library dynamically
  if (!window.msal) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://alcdn.msauth.net/browser/2.38.0/js/msal-browser.min.js';
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load MSAL'));
      document.head.appendChild(script);
    });
  }

  const msalConfig = {
    auth: {
      clientId: MICROSOFT_CLIENT_ID,
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: `${window.location.origin}/auth/microsoft/callback`,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  };

  msalInstance = new window.msal.PublicClientApplication(msalConfig);
  await msalInstance.initialize();
  return msalInstance;
};

/**
 * Trigger Microsoft Sign-In
 */
export const signInWithMicrosoft = async () => {
  const msal = await initMicrosoftAuth();
  
  const loginRequest = {
    scopes: ['User.Read', 'email', 'openid', 'profile'],
  };

  try {
    const response = await msal.loginPopup(loginRequest);
    return response.accessToken;
  } catch (error) {
    throw new Error('Microsoft sign-in failed: ' + error.message);
  }
};

/**
 * Exchange OAuth token with backend for JWT
 */
export const exchangeOAuthToken = async (provider, token, userData = {}) => {
  const endpoints = {
    google: '/auth/oauth/google/',
    apple: '/auth/oauth/apple/',
    microsoft: '/auth/oauth/microsoft/',
  };

  const endpoint = endpoints[provider];
  if (!endpoint) {
    throw new Error(`Unknown OAuth provider: ${provider}`);
  }

  const payload = { token };
  
  // Apple may include user data on first login
  if (provider === 'apple' && userData) {
    payload.user = userData;
  }

  const response = await apiClient.post(endpoint, payload, {
    __skipAuthRefresh: true, // Don't try to refresh auth for login requests
  });

  return response.data;
};

/**
 * Claim an invite with OAuth
 */
export const claimInviteWithOAuth = async (inviteToken, provider, oauthToken, userData = {}) => {
  const response = await apiClient.post('/auth/claim-invite/', {
    token: inviteToken,
    method: 'oauth',
    oauth_provider: provider,
    oauth_token: oauthToken,
    ...userData,
  }, {
    __skipAuthRefresh: true,
  });

  return response.data;
};

/**
 * Claim an invite with password
 */
export const claimInviteWithPassword = async (inviteToken, { username, password, passwordConfirm, fullName }) => {
  const response = await apiClient.post('/auth/claim-invite/', {
    token: inviteToken,
    method: 'password',
    username,
    password,
    password_confirm: passwordConfirm,
    full_name: fullName,
  }, {
    __skipAuthRefresh: true,
  });

  return response.data;
};

/**
 * Validate an invite token
 */
export const validateInvite = async (inviteToken) => {
  const response = await apiClient.get('/auth/validate-invite/', {
    params: { token: inviteToken },
    __skipAuthRefresh: true,
  });

  return response.data;
};

/**
 * Install the JWT pair returned by an OAuth exchange.
 *
 * SECURITY (incident 2026-07-25): OAuth login is a session replacement, the
 * same as password login. Tokens must NEVER be written straight to
 * localStorage from here — that path skipped the cache wipe, the session-
 * scope notification, and the login mark, so a previous tenant's cached
 * agenda could flash under the new identity. `authService.installSession`
 * is the single sanctioned installer: it clears the shared and persistent
 * HTTP caches, notifies session-scope listeners, and stamps the login.
 */
export const installOAuthSession = async (result = {}) => {
  const access = result?.access;
  const refresh = result?.refresh;

  if (access && refresh) {
    await authService.installSession({
      access,
      refresh,
      user: result?.user,
    });
    return result;
  }

  if (access || refresh) {
    // A partial pair cannot form a working session; refuse instead of
    // half-installing credentials the refresh flow can't sustain.
    throw new Error('OAuth login returned an incomplete token pair.');
  }

  // No tokens at all (e.g. backend replied with an error envelope):
  // hand the payload back so the caller can surface the failure.
  return result;
};

/**
 * Full OAuth login flow
 * 1. Get token from provider
 * 2. Exchange with backend
 * 3. Install the session through authService.installSession
 */
export const oauthLogin = async (provider) => {
  let token;
  let userData = {};

  switch (provider) {
    case 'google':
      token = await signInWithGoogle();
      break;
    case 'apple': {
      const appleResult = await signInWithApple();
      token = appleResult.token;
      userData = appleResult.user;
      break;
    }
    case 'microsoft':
      token = await signInWithMicrosoft();
      break;
    default:
      throw new Error(`Unknown provider: ${provider}`);
  }

  // Exchange with backend, then install through the central session
  // installer (cache wipe + scope notification + login mark).
  const result = await exchangeOAuthToken(provider, token, userData);
  return installOAuthSession(result);
};

export default {
  initGoogleAuth,
  signInWithGoogle,
  initAppleAuth,
  signInWithApple,
  initMicrosoftAuth,
  signInWithMicrosoft,
  exchangeOAuthToken,
  claimInviteWithOAuth,
  claimInviteWithPassword,
  validateInvite,
  installOAuthSession,
  oauthLogin,
};
